/**
 * Admin API client — re-embedding utilities and embedding stats.
 */

import { apiClient } from './client';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ReembedTarget = 'ku_l1' | 'ku_l2' | 'ontology_nodes';

export interface ReembedJob {
  id: string;
  target: ReembedTarget;
  status: 'running' | 'completed' | 'failed';
  startedAt: string;
  completedAt?: string;
  total: number;
  embedded: number;
  failed: number;
  message?: string;
  userId: string;
}

export interface EmbeddingLayerStats {
  missing: number;
  total: number;
}

export interface EmbeddingStats {
  ku_l1: EmbeddingLayerStats;
  ku_l2: EmbeddingLayerStats;
  ontology_nodes: EmbeddingLayerStats;
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const adminApi = {
  /** GET /api/admin/embedding-stats — counts of missing embeddings per layer. */
  getEmbeddingStats(): Promise<EmbeddingStats> {
    return apiClient.get<EmbeddingStats>('/api/admin/embedding-stats');
  },

  /**
   * POST /api/admin/reembed — start a background re-embedding job.
   * Returns immediately with the new job.
   */
  startReembedJob(
    target: ReembedTarget,
    opts?: { knowledgeBaseId?: string; ontologyId?: string }
  ): Promise<ReembedJob> {
    return apiClient.post<ReembedJob>('/api/admin/reembed', {
      target,
      ...opts,
    });
  },

  /** GET /api/admin/reembed — list all jobs for the calling user. */
  listReembedJobs(): Promise<ReembedJob[]> {
    return apiClient.get<ReembedJob[]>('/api/admin/reembed');
  },

  /** GET /api/admin/reembed/:jobId — poll a specific job. */
  getReembedJob(jobId: string): Promise<ReembedJob> {
    return apiClient.get<ReembedJob>(`/api/admin/reembed/${jobId}`);
  },
};

// ─── User-scoped API (no admin role required) ─────────────────────────────────
// These mirror the admin endpoints but are available to all authenticated users.
// They are scoped to the calling user's own KBs and ontologies.

export const meEmbeddingsApi = {
  /** GET /api/me/embeddings/stats */
  getStats(): Promise<EmbeddingStats> {
    return apiClient.get<EmbeddingStats>('/api/me/embeddings/stats');
  },

  /** POST /api/me/embeddings/reembed */
  startReembedJob(
    target: ReembedTarget,
    opts?: { knowledgeBaseId?: string; ontologyId?: string }
  ): Promise<ReembedJob> {
    return apiClient.post<ReembedJob>('/api/me/embeddings/reembed', { target, ...opts });
  },

  /** GET /api/me/embeddings/jobs */
  listJobs(): Promise<ReembedJob[]> {
    return apiClient.get<ReembedJob[]>('/api/me/embeddings/jobs');
  },

  /** GET /api/me/embeddings/jobs/:jobId */
  getJob(jobId: string): Promise<ReembedJob> {
    return apiClient.get<ReembedJob>(`/api/me/embeddings/jobs/${jobId}`);
  },
};

// Made with Bob
